import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useGTM,
  useQuery,
  useUserOrders,
  useBannerVisibility,
  useCreationDisabled
} from 'hooks';
import { useFeatureFlag, FLAGS } from 'lib/featureFlags';
import { Loader } from '@teespring/ts-components';
import get from 'lodash/get';
import { delightedScript } from 'lib/delighted';
import useScript from 'hooks/useScripts';
// import useOnboardingChecklist from 'hooks/useOnboardingChecklist';
import axios from 'axios';
import WhatsNew from './WhatsNew';
import VerificationToast from './VerificationToast';
import EmailVerifyBar from './EmailVerifyBar';
import { API_HOST } from '../../constants';
// import OnboardingChecklist from './OnboardingChecklist';

import './Overview.scss';
import './Overview.css';

import IntegrationShelf from './IntegrationShelf';
import PromoBanner from './PromoBanner';
// import AnalyticsOverview from './AnalyticsOverview';

// LAU-170: from the moment creation is disabled, Home carries the top banner
// and nothing else -- no Sell anywhere carousel, no Quick actions, no Sample
// or Discord banner. Everything else here either promotes a feature that has
// been switched off or links to a route the guard now blocks.
//
// The set does not change again at payout-only mode -- both steps of the
// shutdown show this one banner, so there is no second reduction to get wrong.
const TOP_BANNER_ID = 'spring_retired_banner';

// LAU-172 destination. No UTM params required.
//
// The ticket gives amaze-commerce.webflow.io/spring-update, which is the
// Webflow staging origin rather than the published site. The banner keeps the
// production URL it already pointed at -- same page, same path.
const SPRING_UPDATE_URL = 'https://www.amazecommerce.com/spring-update';

// LAU-63 banner destination (per ticket, no UTM params)
const MOVE_YOUR_STORE_URL = 'https://amazecommerce.com/';

const Overview = () => {
  const emailVerificationEnabled = useFeatureFlag(FLAGS.EMAIL_VERIFICATION);
  // const [displayChecklist, setDisplayChecklist] = useState(true);

  // const { dismissed, dismissChecklist, checklist, loading } =
  //   useOnboardingChecklist();
  // useEffect(() => {
  //   setDisplayChecklist(!dismissed);
  // }, [dismissed]);
  const user = useSelector((state) => get(state, 'header.user'));
  const { userId } = useSelector((state) => state.user);
  // const handleDismissClick = () => {
  //   dismissChecklist();
  // };

  const isFetchingUserSubscriptions = useSelector(
    (state) => state.subscriptions.isFetchingUserSubscriptions
  );

  const { query } = useQuery();
  const { pushAccountToDataLayer } = useGTM();

  const { verified, email } = user;
  // Payout-only mode implies creation disabled, so this one flag covers both
  // steps of the shutdown. Home looks the same at each.
  const creationDisabled = useCreationDisabled();

  // const recentlySignedUpUser = useSelector((state) =>
  //   get(state, 'user.recentlySignedUp', false)
  // );

  const needVerifyEmailBar =
    !query.get('verified') && emailVerificationEnabled && !verified;

  useEffect(() => {
    pushAccountToDataLayer();
  }, [pushAccountToDataLayer]);

  // delighted survey
  const { orders } = useUserOrders(user.id);

  const [listings, setListings] = useState([]);
  useEffect(() => {
    const fetchListings = async () => {
      const perPage = 10;
      const { data } = await axios.get(`${API_HOST}/api/v1/user/listings`, {
        withCredentials: true,
        params: {
          userId,
          per: perPage,
          page: 1,
          sortBy: 'createdDate',
          orderBy: 'DESC'
        }
      });
      return data.listings;
    };
    fetchListings().then(setListings);
  }, [userId]);

  useScript(
    delightedScript(user, listings?.length > 0, orders && orders?.length > 0)
  );

  // Banner active from 9:00 AM PST, December 12th, 2024 to 9:00 AM PST, December 23rd, 2024
  const isLastMinuteHolidaySaleVisible = useBannerVisibility(
    '2024-12-12T17:00:00Z',
    '2024-12-23T17:00:00Z'
  );

  // Banner active from November 1st, 2024, 9:00 AM PST (4:00 PM UTC) to end of December 31, 2024 (midnight PST)
  // const isHolidayBannerVisible = useBannerVisibility(
  //   '2024-11-01T16:00:00Z',
  //   '2025-01-01T08:00:00Z'
  // );

  // LAU-172: replaces the "closing on September 15" notice, whose date has now
  // passed, and points creators at Amaze Commerce for new listings. This is a
  // straight swap rather than a flagged variant: the old copy is wrong whether
  // or not creation has been disabled yet.
  const upperBanners = [
    {
      id: TOP_BANNER_ID,
      desktopSrc: require('../../assets/spring_retired_desktop.jpg'),
      mobileSrc: require('../../assets/spring_retired_mobile.jpg'),
      alt:
        'Spring is being retired. Keep managing your listings. ' +
        'Launch new ones on Amaze Commerce.',
      link: SPRING_UPDATE_URL,
      isVisible: true
    },
    {
      id: 'move_your_store_banner',
      desktopSrc: require('../../assets/move_your_store_desktop.png'),
      mobileSrc: require('../../assets/move_your_store_mobile.png'),
      alt: 'Move your storefront before Sept 15 - Get Started Free',
      link: MOVE_YOUR_STORE_URL,
      isVisible: true
    },
    // {
    //   id: 'redemption_banner_adobe_express',
    //   desktopSrc: require('../../assets/dt_overview_spring_gtm.png'),
    //   mobileSrc: require('../../assets/banner_adobe_344x294.5.png'),
    //   alt: 'Adobe express redemption banner',
    //   link: 'https://www.amaze.co/adobe-redemption',
    //   isVisible: isBannerVisible
    // },
    {
      id: 'promo_banner_last_minute_holiday_sale',
      desktopSrc: require('../../assets/last-minute-holiday-sale_desktop.png'),
      mobileSrc: require('../../assets/last-minute-holiday-sale_mobile.png'),
      alt: 'last minute holiday sale',
      link: '',
      isVisible: isLastMinuteHolidaySaleVisible
    },
    {
      id: 'promo_banner_samples_increase_sales',
      desktopSrc: require('../../assets/ThreeTimesSamples_Desktop.png'),
      mobileSrc: require('../../assets/ThreeTimesSamples_Mobile.png'),
      alt: 'samples increase sales',
      link: 'https://dashboard.teespring.com/samples',
      isVisible: true
    }
  ];

  const lowerBanners = [
    // {
    //   id: 'promo_banner_college_ambassadors',
    //   desktopSrc: require('../../assets/dt_college_ambassadors.png'),
    //   mobileSrc: require('../../assets/mob_college_ambassadors.png'),
    //   alt: 'college ambassadors',
    //   link: 'https://share.hsforms.com/1HoP2rgLuR_STYT9qOt1qVQe19c5'
    // },
    {
      id: 'promo_banner_discord',
      desktopSrc: require('../../assets/discord-dashboard_desktop.png'),
      mobileSrc: require('../../assets/discord-dashboard_mobile.png'),
      alt: 'discord hub',
      link: 'https://discord.gg/Rb2QkpSu'
    }
  ];

  // Filter banners based on visibility
  const isVisibleInCurrentMode = (banner) =>
    banner.isVisible && (!creationDisabled || banner.id === TOP_BANNER_ID);

  const visibleUpperBanners = upperBanners.filter(isVisibleInCurrentMode);
  const visibleLowerBanners = creationDisabled ? [] : lowerBanners;

  return isFetchingUserSubscriptions ? (
    <Loader />
  ) : (
    <div className='overview_cont'>
      <div className='overview'>
        <VerificationToast className='overview__verification__toast' />
        <section className='overview__content'>
          {needVerifyEmailBar && <EmailVerifyBar email={email} />}
          <div className='firstBanner'>
            <PromoBanner banners={visibleUpperBanners} />
          </div>
          <div className='secondBanner'>
            {/* {!recentlySignedUpUser && <AnalyticsOverview />} */}
          </div>
        </section>
      </div>
      {!creationDisabled && (
        <div className='thirdBanner'>
          <IntegrationShelf />
          <WhatsNew className='fourthBanner' />
        </div>
      )}
      {/* .fifthBanner carries a 32px top margin and .overview its own padding,
          so rendering it empty would leave a visible gap under the banner. */}
      {visibleLowerBanners.length > 0 && (
        <div className='fifthBanner'>
          <div className='overview'>
            <PromoBanner banners={visibleLowerBanners} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
